import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Plus, Eye, Edit2, Trash2, Search, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext.jsx';
import EnhancedEmailComposer from './email/EnhancedEmailComposer';

const EmailCampaigns = ({ contacts, settings }) => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignStats, setCampaignStats] = useState({
    totalCampaigns: 0,
    sentCampaigns: 0,
    averageOpenRate: 0,
    averageClickRate: 0
  });
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignFilters, setCampaignFilters] = useState({
    search: '',
    status: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Get contacts with valid emails for email campaigns
  const emailContacts = contacts.filter(contact => contact.email);

  // Fetch campaigns from backend
  const fetchCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/comms-marketing/email-campaigns`, {
        params: campaignFilters
      });
      setCampaigns(response.data.campaigns || []);
      setCampaignStats(response.data.stats || {
        totalCampaigns: 0,
        sentCampaigns: 0,
        averageOpenRate: 0,
        averageClickRate: 0
      });
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      } else {
        toast.error('Failed to load email campaigns');
      }
    }
  }, [campaignFilters]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // 🆕 PHASE 3: Handler for saving campaign
  const handleSaveCampaign = async (campaignData) => {
    try {
      if (editingCampaign) {
        // Update existing campaign
        await axios.put(
          `${import.meta.env.VITE_API_URL}/api/comms-marketing/email-campaigns/${editingCampaign._id}`,
          campaignData
        );
        toast.success('Campaign updated successfully!');
      } else {
        // Create new campaign
        await axios.post(
          `${import.meta.env.VITE_API_URL}/api/comms-marketing/email-campaigns`,
          campaignData
        );
        toast.success('Campaign saved as draft!');
      }
      
      // Refresh campaigns list
      await fetchCampaigns();
      setShowBuilder(false);
      setEditingCampaign(null);
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error('Failed to save campaign: ' + (error.response?.data?.message || error.message));
    }
  };

  // 🆕 PHASE 3: Handler for sending campaign
  const handleSendCampaign = async (campaignData) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/email-campaigns/send`,
        campaignData
      );
      
      toast.success(`Campaign sent to ${campaignData.recipientIds.length} recipient(s)!`);
      
      // Refresh campaigns list
      await fetchCampaigns();
      setShowBuilder(false);
      setEditingCampaign(null);
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error('Failed to send campaign: ' + (error.response?.data?.message || error.message));
    }
  };

  // Handle edit campaign
  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setShowBuilder(true);
  };

  // Handle delete campaign
  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(`${import.meta.env.VITE_API_URL}/api/comms-marketing/email-campaigns/${campaignId}`);
      toast.success('Campaign deleted successfully');
      await fetchCampaigns();
    } catch (err) {
      console.error('Error deleting campaign:', err);
      toast.error('Failed to delete campaign');
    }
  };

  // Utility functions
  const formatDate = (date) => date ? new Date(date).toLocaleDateString() : '-';

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'sending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Pagination logic
  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesSearch = !campaignFilters.search || 
      campaign.name.toLowerCase().includes(campaignFilters.search.toLowerCase()) ||
      campaign.subject.toLowerCase().includes(campaignFilters.search.toLowerCase());
    
    const matchesStatus = !campaignFilters.status || campaign.status === campaignFilters.status;
    
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);
  const paginatedCampaigns = filteredCampaigns.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // If builder is shown, render the EnhancedEmailComposer
  if (showBuilder) {
    return (
      <div>
        {/* Back Button */}
        <button
          onClick={() => {
            setShowBuilder(false);
            setEditingCampaign(null);
          }}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition"
        >
          <ArrowLeft size={20} />
          Back to Campaigns
        </button>

        {/* 🆕 PHASE 3: Enhanced Email Composer */}
        <EnhancedEmailComposer
          contacts={emailContacts}
          settings={settings}
          initialData={editingCampaign}
          onSave={handleSaveCampaign}
          onSend={handleSendCampaign}
        />
      </div>
    );
  }

  // Main view (campaigns list)
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Total Campaigns</h3>
          <p className="text-2xl font-bold text-blue-600">{campaignStats.totalCampaigns}</p>
          <p className="text-xs text-gray-600 mt-2">All time</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Sent Campaigns</h3>
          <p className="text-2xl font-bold text-green-600">{campaignStats.sentCampaigns}</p>
          <p className="text-xs text-gray-600 mt-2">Successfully delivered</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Avg Open Rate</h3>
          <p className="text-2xl font-bold text-purple-600">{campaignStats.averageOpenRate}%</p>
          <p className="text-xs text-gray-600 mt-2">Email opens</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-2">Avg Click Rate</h3>
          <p className="text-2xl font-bold text-yellow-600">{campaignStats.averageClickRate}%</p>
          <p className="text-xs text-gray-600 mt-2">Link clicks</p>
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap justify-between items-center gap-4">
          <button
            onClick={() => {
              setEditingCampaign(null);
              setShowBuilder(true);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-2 transition"
          >
            <Plus size={16} />
            Create Email Campaign
          </button>
          <p className="text-sm text-gray-600">{emailContacts.length} email contacts available</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={campaignFilters.search}
              onChange={(e) => setCampaignFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>
          <select
            value={campaignFilters.status}
            onChange={(e) => setCampaignFilters(prev => ({ ...prev, status: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="sent">Sent</option>
            <option value="sending">Sending</option>
          </select>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Email Campaigns</h3>
          <p className="text-sm text-gray-600 mt-1">
            Showing {paginatedCampaigns.length} of {filteredCampaigns.length} campaigns
          </p>
        </div>
        {paginatedCampaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipients
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedCampaigns.map((campaign) => (
                  <tr key={campaign._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                      <div className="text-xs text-gray-500">{campaign.subject}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(campaign.status)}`}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {campaign.recipients || campaign.recipientIds?.length || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(campaign.createdAt)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            alert(`Campaign: ${campaign.name}\n\nSubject: ${campaign.subject}\n\nStatus: ${campaign.status}\nRecipients: ${campaign.recipients || campaign.recipientIds?.length || 0}`);
                          }}
                          className="text-blue-600 hover:text-blue-900 transition"
                          title="Preview"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEditCampaign(campaign)}
                          className="text-green-600 hover:text-green-900 transition"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteCampaign(campaign._id)}
                          className="text-red-600 hover:text-red-900 transition"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center">
            <Mail size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Email Campaigns Yet</h3>
            <p className="text-gray-600 mb-4">
              Create your first email campaign with our professional email composer.
            </p>
            <button
              onClick={() => {
                setEditingCampaign(null);
                setShowBuilder(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Create Your First Campaign
            </button>
          </div>
        )}
        {totalPages > 1 && (
          <div className="p-4 flex justify-between items-center border-t">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailCampaigns;